/**
 * The owner's enquiries desk (14 Oct 2026): what the screen reads from
 * /enquiry-desk — everyone's enquiries across the books, by person, with
 * every call back, meeting and outcome as a feed. Read-only; the writing
 * stays in the enquiry book.
 */
import { useQuery } from '@tanstack/react-query';
import type { EnquiryOutcomeKey } from '@bright/shared';

import { api } from '@/lib/api';

export type DeskPeriod = 1 | 7 | 30;
export type DeskEventKind = 'NEW' | 'UPDATE' | 'FOLLOW_UP' | 'WON' | 'LOST' | 'MEETING';
export type DeskTab = 'due' | 'open' | 'won' | 'lost' | 'all';

export interface DeskPerson {
  userId: string | null;
  name: string;
  role: string | null;
  book: string | null;
  open: number;
  overdue: number;
  dueToday: number;
  updates: number;
  fresh: number;
  won: number;
  lost: number;
  meetings: number;
  lastActivityAt: string | null;
}

export interface DeskOverview {
  period: { days: DeskPeriod; from: string; to: string };
  people: DeskPerson[];
  totals: { open: number; overdue: number; dueToday: number; updates: number; fresh: number; won: number; lost: number; meetings: number };
}

export interface DeskEnquiryBit {
  id: string;
  customerName: string;
  phone: string | null;
  wanted: string | null;
  area: string | null;
  outcome: EnquiryOutcomeKey;
  followUpAt: string | null;
  book: string;
}

export interface DeskEvent {
  id: string;
  kind: DeskEventKind;
  at: string;
  by: { id: string | null; name: string | null };
  enquiry: DeskEnquiryBit;
  note: string | null;
  followUpAt: string | null;
}

export interface DeskEnquiryRow extends DeskEnquiryBit {
  source: string;
  channel: string | null;
  createdAt: string;
  settledAt: string | null;
  outcomeNote: string | null;
  takenBy: string | null;
  updates: number;
  lastUpdate: { at: string; by: string | null; note: string | null } | null;
}

export interface DeskStep {
  id: string;
  kind: DeskEventKind;
  at: string;
  by: string | null;
  note: string | null;
  followUpAt: string | null;
}

export interface DeskEnquiryDetail extends DeskEnquiryRow {
  story: DeskStep[];
}

export const KIND_LABELS: Record<DeskEventKind, string> = {
  NEW: 'New enquiry',
  UPDATE: 'Call back',
  FOLLOW_UP: 'Next call booked',
  WON: 'Won',
  LOST: 'Lost',
  MEETING: 'Met in the field',
};
export const KIND_TONE: Record<DeskEventKind, 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info' | 'signal'> = {
  NEW: 'accent',
  UPDATE: 'info',
  FOLLOW_UP: 'warning',
  WON: 'success',
  LOST: 'neutral',
  MEETING: 'signal',
};

export const deskKeys = {
  all: ['enquiry-desk'] as const,
  overview: (days: DeskPeriod) => ['enquiry-desk', 'overview', days] as const,
  feed: (days: DeskPeriod, userId: string, page: number) => ['enquiry-desk', 'feed', days, userId, page] as const,
  enquiries: (userId: string, tab: DeskTab, page: number) => ['enquiry-desk', 'enquiries', userId, tab, page] as const,
  enquiry: (id: string) => ['enquiry-desk', 'enquiry', id] as const,
};

export function useDeskOverview(days: DeskPeriod, enabled: boolean) {
  return useQuery({
    queryKey: deskKeys.overview(days),
    queryFn: () => api.get<DeskOverview>('/enquiry-desk/overview', { days }, { book: null }),
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    placeholderData: (previous) => previous,
  });
}

export function useDeskFeed(days: DeskPeriod, userId: string, page: number, enabled: boolean) {
  return useQuery({
    queryKey: deskKeys.feed(days, userId, page),
    queryFn: () => api.get<{ data: DeskEvent[]; meta: { page: number; pageSize: number; total: number } }>('/enquiry-desk/feed', { days, page, ...(userId ? { userId } : {}) }, { book: null }),
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    placeholderData: (previous) => previous,
  });
}

export function useDeskEnquiries(userId: string, tab: DeskTab, page: number) {
  return useQuery({
    queryKey: deskKeys.enquiries(userId, tab, page),
    queryFn: () => api.get<{ data: DeskEnquiryRow[]; meta: { page: number; pageSize: number; total: number }; tabs: Record<DeskTab, number> }>(`/enquiry-desk/people/${userId}/enquiries`, { tab, page }, { book: null }),
    enabled: userId !== '',
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });
}

export function useDeskEnquiry(id: string | null) {
  return useQuery({
    queryKey: deskKeys.enquiry(id ?? ''),
    queryFn: () => api.get<DeskEnquiryDetail>(`/enquiry-desk/enquiries/${id}`, undefined, { book: null }),
    enabled: id !== null,
    staleTime: 30_000,
  });
}

/** "Tue 14 Oct, 11:20" in India. */
export function whenLabel(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' })}, ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' })}`;
}

/** "2 h ago", "yesterday", "5 d ago" — how fresh a person's last activity is. */
export function agoLabel(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'yesterday';
  return `${d} d ago`;
}
